import React, { useState, useEffect, lazy, Suspense } from 'react';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import {
  RiFileTextLine,
  RiDownloadLine,
  RiEditLine,
  RiCheckLine,
  RiAlertLine,
  RiMagicLine,
  RiEyeLine,
  RiEyeOffLine,
  RiAttachmentLine,
  RiRefreshLine,
  RiArrowDownSLine,
  RiArrowRightSLine,
  RiFileCodeLine,
  RiFileCopyLine,
} from 'react-icons/ri';
import { formsApi } from '@/api';

// Lazy load PDFViewer for iPad/iOS compatibility
const PDFViewer = lazy(() => import('@/components/ui/PDFViewer'));

interface FormsTabProps {
  caseId: string;
  caseData?: {
    applicantName?: string;
    visaType?: string;
    nationality?: string;
    email?: string;
    phone?: string;
  };
}

// Form types and visa category mappings
type FormType = 'i140' | 'i129' | 'i907' | 'g28';

interface FormInfo {
  id: FormType;
  name: string;
  fullName: string;
  description: string;
}

// Base form definitions - I-907 description is determined dynamically by visa type
const ALL_FORMS: FormInfo[] = [
  {
    id: 'g28',
    name: 'G-28',
    fullName: 'Form G-28',
    description: 'Attorney Representation',
  },
  {
    id: 'i140',
    name: 'I-140',
    fullName: 'Form I-140',
    description: 'Immigrant Worker Petition',
  },
  {
    id: 'i129',
    name: 'I-129',
    fullName: 'Form I-129',
    description: 'Nonimmigrant Worker Petition',
  },
  {
    id: 'i907',
    name: 'I-907',
    fullName: 'Form I-907',
    description: 'Premium Processing Request',
  },
];

// Get dynamic I-907 description based on visa type
function getI907Description(visaType?: string): string {
  if (!visaType) return 'Premium Processing Request';

  const normalizedVisa = visaType.toLowerCase();

  // O-1 and other non-immigrant visas use I-129
  if (normalizedVisa.includes('o-1') || normalizedVisa.includes('h-1b')) {
    return 'Premium Processing for I-129';
  }

  // EB visas use I-140
  if (normalizedVisa.includes('eb-')) {
    return 'Premium Processing for I-140';
  }

  return 'Premium Processing Request';
}

// Visa type to form mapping - Featured forms by visa category
const VISA_FORM_MAPPING: Record<string, FormType[]> = {
  // Employment-based (EB) - use I-140 (immigrant petition)
  'eb-1a': ['g28', 'i140', 'i907'],
  'eb-1b': ['g28', 'i140', 'i907'],
  'eb-1c': ['g28', 'i140', 'i907'],
  'eb-2': ['g28', 'i140', 'i907'],
  'eb-2-niw': ['g28', 'i140', 'i907'],
  'eb-3': ['g28', 'i140', 'i907'],
  // Non-immigrant workers - use I-129 (nonimmigrant petition)
  'o-1a': ['g28', 'i129', 'i907'],
  'o-1b': ['g28', 'i129', 'i907'],
  'o-1': ['g28', 'i129', 'i907'],
  'h-1b': ['g28', 'i129', 'i907'],
  // Default - show all forms
  default: ['g28', 'i140', 'i129', 'i907'],
};

function getFeaturedForms(visaType?: string): FormType[] {
  if (!visaType) return VISA_FORM_MAPPING['default'];

  const normalizedVisa = visaType.toLowerCase().replace(/\s+/g, '-');

  // Try exact match
  if (VISA_FORM_MAPPING[normalizedVisa]) {
    return VISA_FORM_MAPPING[normalizedVisa];
  }

  // Try partial match
  for (const key of Object.keys(VISA_FORM_MAPPING)) {
    if (normalizedVisa.includes(key) || key.includes(normalizedVisa)) {
      return VISA_FORM_MAPPING[key];
    }
  }

  return VISA_FORM_MAPPING['default'];
}

interface FormData {
  // I-140 Fields
  i140: {
    beneficiaryLastName: string;
    beneficiaryFirstName: string;
    beneficiaryMiddleName: string;
    dateOfBirth: string;
    countryOfBirth: string;
    countryOfCitizenship: string;
    alienRegistrationNumber: string;
    currentNonimmigrantStatus: string;
    dateOfLastArrival: string;
    i94Number: string;
    passportNumber: string;
    passportCountry: string;
    passportExpiration: string;
    currentAddress: string;
    city: string;
    state: string;
    zipCode: string;
    email: string;
    phone: string;
    classificationRequested: string;
  };
  // I-129 Fields (O-1 Nonimmigrant Worker)
  i129: {
    // Petitioner Info
    petitionerName: string;
    petitionerEIN: string;
    petitionerAddress: string;
    petitionerCity: string;
    petitionerState: string;
    petitionerZipCode: string;
    petitionerPhone: string;
    petitionerContactName: string;
    petitionerContactTitle: string;
    // Beneficiary Info
    beneficiaryLastName: string;
    beneficiaryFirstName: string;
    beneficiaryMiddleName: string;
    dateOfBirth: string;
    countryOfBirth: string;
    countryOfCitizenship: string;
    alienRegistrationNumber: string;
    currentNonimmigrantStatus: string;
    dateOfLastArrival: string;
    i94Number: string;
    passportNumber: string;
    passportCountry: string;
    passportExpiration: string;
    currentAddress: string;
    city: string;
    state: string;
    zipCode: string;
    email: string;
    phone: string;
    // Classification
    classificationRequested: string;
    // Employment
    jobTitle: string;
    workStartDate: string;
    workEndDate: string;
    wageOffered: string;
    hoursPerWeek: string;
    workLocationAddress: string;
    workLocationCity: string;
    workLocationState: string;
    workLocationZipCode: string;
  };
  // I-907 Premium Processing
  i907: {
    enabled: boolean;
    beneficiaryName: string;
    receiptNumber: string;
  };
  // G-28 Attorney
  g28: {
    enabled: boolean;
    attorneyLastName: string;
    attorneyFirstName: string;
    lawFirmName: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
    phone: string;
    email: string;
    barNumber: string;
    USCISOnlineAccountNumber: string;
  };
}

// Wizard steps definitions
const I140_STEPS = [
  {
    id: 'personal',
    title: 'Personal Info',
    description: 'Name and basic information',
  },
  {
    id: 'birth',
    title: 'Birth Information',
    description: 'Date and place of birth',
  },
  {
    id: 'immigration',
    title: 'Immigration Status',
    description: 'Current status and history',
  },
  {
    id: 'passport',
    title: 'Passport Details',
    description: 'Travel document information',
  },
  { id: 'contact', title: 'Contact Info', description: 'Address and phone' },
  {
    id: 'classification',
    title: 'Classification',
    description: 'Visa category requested',
  },
];

const I129_STEPS = [
  {
    id: 'petitioner',
    title: 'Petitioner (Employer)',
    description: 'Company information',
  },
  {
    id: 'beneficiary',
    title: 'Beneficiary Info',
    description: 'Worker details',
  },
  {
    id: 'classification',
    title: 'Classification',
    description: 'O-1 visa type',
  },
  {
    id: 'employment',
    title: 'Employment Details',
    description: 'Job and wages',
  },
  {
    id: 'location',
    title: 'Work Location',
    description: 'Where work will be performed',
  },
];

const FormsTab: React.FC<FormsTabProps> = ({ caseId, caseData }) => {
  // Get featured forms based on visa type
  const featuredFormIds = getFeaturedForms(caseData?.visaType);
  const featuredForms = ALL_FORMS.filter(f => featuredFormIds.includes(f.id));
  const otherForms = ALL_FORMS.filter(f => !featuredFormIds.includes(f.id));

  // Set initial active form to first featured form
  const [activeForm, setActiveForm] = useState<FormType>(
    () => featuredFormIds[0] || 'g28'
  );

  // Wizard step states
  const [i140Step, setI140Step] = useState(0);
  const [i129Step, setI129Step] = useState(0);

  // Other forms expansion state
  const [showOtherForms, setShowOtherForms] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    i140: {
      beneficiaryLastName: '',
      beneficiaryFirstName: '',
      beneficiaryMiddleName: '',
      dateOfBirth: '',
      countryOfBirth: '',
      countryOfCitizenship: '',
      alienRegistrationNumber: '',
      currentNonimmigrantStatus: '',
      dateOfLastArrival: '',
      i94Number: '',
      passportNumber: '',
      passportCountry: '',
      passportExpiration: '',
      currentAddress: '',
      city: '',
      state: '',
      zipCode: '',
      email: caseData?.email || '',
      phone: caseData?.phone || '',
      classificationRequested: caseData?.visaType || '',
    },
    i129: {
      // Petitioner Info
      petitionerName: '',
      petitionerEIN: '',
      petitionerAddress: '',
      petitionerCity: '',
      petitionerState: '',
      petitionerZipCode: '',
      petitionerPhone: '',
      petitionerContactName: '',
      petitionerContactTitle: '',
      // Beneficiary Info
      beneficiaryLastName: '',
      beneficiaryFirstName: '',
      beneficiaryMiddleName: '',
      dateOfBirth: '',
      countryOfBirth: '',
      countryOfCitizenship: '',
      alienRegistrationNumber: '',
      currentNonimmigrantStatus: '',
      dateOfLastArrival: '',
      i94Number: '',
      passportNumber: '',
      passportCountry: '',
      passportExpiration: '',
      currentAddress: '',
      city: '',
      state: '',
      zipCode: '',
      email: caseData?.email || '',
      phone: caseData?.phone || '',
      // Classification
      classificationRequested: caseData?.visaType || '',
      // Employment
      jobTitle: '',
      workStartDate: '',
      workEndDate: '',
      wageOffered: '',
      hoursPerWeek: '',
      workLocationAddress: '',
      workLocationCity: '',
      workLocationState: '',
      workLocationZipCode: '',
    },
    i907: {
      enabled: false,
      beneficiaryName: '',
      receiptNumber: '',
    },
    g28: {
      enabled: false,
      attorneyLastName: '',
      attorneyFirstName: '',
      lawFirmName: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      phone: '',
      email: '',
      barNumber: '',
      USCISOnlineAccountNumber: '',
    },
  });

  const [isSaving, setIsSaving] = useState(false);
  const [isRefilling, setIsRefilling] = useState(false);
  const [saveStatus, setSaveStatus] = useState<
    'idle' | 'saving' | 'saved' | 'error'
  >('idle');
  const [isLoading, setIsLoading] = useState(false);

  // PDF Preview state
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState<string | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isSavingToCase, setIsSavingToCase] = useState(false);

  // Form capability info
  const [previewInfo, setPreviewInfo] = useState<{
    supportsFilling: boolean;
    filledFieldCount: number;
    totalFieldCount: number;
  } | null>(null);

  // Extra fields from API that aren't in predefined FormData
  const [extraFields, setExtraFields] = useState<
    Record<string, Record<string, any>>
  >({});
  const [showExtraFields, setShowExtraFields] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // Copy value to clipboard with visual feedback
  const copyToClipboard = async (key: string, value: unknown) => {
    const textValue =
      typeof value === 'boolean'
        ? value
          ? 'Yes'
          : 'No'
        : typeof value === 'object'
          ? JSON.stringify(value)
          : String(value || '');

    try {
      await navigator.clipboard.writeText(textValue);
      setCopiedField(key);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Define which fields are predefined for each form type
  const PREDEFINED_FIELDS: Record<string, string[]> = {
    i140: [
      'beneficiaryLastName',
      'beneficiaryFirstName',
      'beneficiaryMiddleName',
      'dateOfBirth',
      'countryOfBirth',
      'countryOfCitizenship',
      'alienRegistrationNumber',
      'currentNonimmigrantStatus',
      'dateOfLastArrival',
      'i94Number',
      'passportNumber',
      'passportCountry',
      'passportExpiration',
      'currentAddress',
      'city',
      'state',
      'zipCode',
      'email',
      'phone',
      'classificationRequested',
    ],
    i129: [
      'petitionerName',
      'petitionerEIN',
      'petitionerAddress',
      'petitionerCity',
      'petitionerState',
      'petitionerZipCode',
      'petitionerPhone',
      'petitionerContactName',
      'petitionerContactTitle',
      'beneficiaryLastName',
      'beneficiaryFirstName',
      'beneficiaryMiddleName',
      'dateOfBirth',
      'countryOfBirth',
      'countryOfCitizenship',
      'alienRegistrationNumber',
      'currentNonimmigrantStatus',
      'dateOfLastArrival',
      'i94Number',
      'passportNumber',
      'passportCountry',
      'passportExpiration',
      'currentAddress',
      'city',
      'state',
      'zipCode',
      'email',
      'phone',
      'classificationRequested',
      'jobTitle',
      'workStartDate',
      'workEndDate',
      'wageOffered',
      'hoursPerWeek',
      'workLocationAddress',
      'workLocationCity',
      'workLocationState',
      'workLocationZipCode',
    ],
    i907: ['enabled', 'beneficiaryName', 'receiptNumber'],
    g28: [
      'enabled',
      'attorneyLastName',
      'attorneyFirstName',
      'lawFirmName',
      'address',
      'city',
      'state',
      'zipCode',
      'phone',
      'email',
      'barNumber',
      'USCISOnlineAccountNumber',
    ],
  };

  // Helper to format field name for display (human-readable labels)
  const formatFieldName = (key: string): string => {
    // Common prefixes to strip (from API field naming convention)
    const prefixesToStrip = [
      'PERSONALINFORMATION',
      'PERSONAL_INFORMATION',
      'EMPLOYMENT/JOBINFORMATION',
      'EMPLOYMENT_JOBINFORMATION',
      'EMPLOYMENTJOBINFORMATION',
      'CURRENTUSADDRESS',
      'CURRENT_US_ADDRESS',
      'FOREIGN/HOMECOUNTRYADDRESS',
      'FOREIGN_HOMECOUNTRYADDRESS',
      'FOREIGNHOMECOUNTRYADDRESS',
      'IDENTIFICATIONDOCUMENTS',
      'IDENTIFICATION_DOCUMENTS',
      'CONTACTINFORMATION',
      'CONTACT_INFORMATION',
      'PETITIONERINFO',
      'PETITIONER_INFO',
      'BENEFICIARYINFO',
      'BENEFICIARY_INFO',
      'EMPLOYERINFO',
      'EMPLOYER_INFO',
    ];

    let fieldName = key;

    // Strip known prefixes (case insensitive)
    for (const prefix of prefixesToStrip) {
      const regex = new RegExp(`^${prefix}\\s*`, 'i');
      if (regex.test(fieldName)) {
        fieldName = fieldName.replace(regex, '');
        break;
      }
    }

    // Also handle slash-separated prefixes like "EMPLOYMENT/JOB"
    if (fieldName.includes('/')) {
      const parts = fieldName.split('/');
      fieldName = parts[parts.length - 1]; // Take the last part after slash
    }

    // Convert camelCase to Title Case with spaces
    fieldName = fieldName
      // Add space before uppercase letters (camelCase -> Camel Case)
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      // Handle consecutive uppercase (USA -> USA, USAddress -> US Address)
      .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
      // Replace underscores with spaces
      .replace(/_/g, ' ')
      // Capitalize first letter of each word
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
      .trim();

    // Fix common abbreviations that should stay uppercase
    fieldName = fieldName
      .replace(/\bUs\b/g, 'US')
      .replace(/\bUsa\b/g, 'USA')
      .replace(/\bId\b/g, 'ID')
      .replace(/\bSsn\b/g, 'SSN')
      .replace(/\bEin\b/g, 'EIN')
      .replace(/\bUscis\b/g, 'USCIS')
      .replace(/\bDob\b/g, 'Date of Birth')
      .replace(/\bZip\b/g, 'ZIP');

    return fieldName || key; // Fallback to original if empty
  };

  // Fetch form data from API
  useEffect(() => {
    const fetchFormData = async () => {
      setIsLoading(true);
      try {
        const data = await formsApi.getFormData(caseId, activeForm);

        // Sanitize nulls to empty strings for React inputs
        const sanitizedData = Object.entries(data).reduce(
          (acc, [key, val]) => ({
            ...acc,
            [key]: val === null ? '' : val,
          }),
          {}
        );

        // Separate predefined fields from extra fields
        const predefinedFieldSet = new Set(PREDEFINED_FIELDS[activeForm] || []);
        const extra: Record<string, any> = {};

        Object.entries(sanitizedData).forEach(([key, value]) => {
          // Skip empty values and nested objects for extra fields display
          if (value === '' || value === null || value === undefined) return;
          if (!predefinedFieldSet.has(key)) {
            // For nested objects, flatten them
            if (typeof value === 'object' && value !== null) {
              Object.entries(value as Record<string, any>).forEach(
                ([nestedKey, nestedValue]) => {
                  if (nestedValue && nestedValue !== '') {
                    extra[`${key}.${nestedKey}`] = nestedValue;
                  }
                }
              );
            } else {
              extra[key] = value;
            }
          }
        });

        setExtraFields(prev => ({
          ...prev,
          [activeForm]: extra,
        }));

        setFormData(prev => ({
          ...prev,
          [activeForm]: {
            ...prev[activeForm],
            ...sanitizedData,
          },
        }));
      } catch (error) {
        console.error('Failed to fetch form data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (caseId) {
      fetchFormData();
    }
  }, [caseId, activeForm]);

  const handleInputChange = (
    form: 'i140' | 'i129' | 'i907' | 'g28',
    field: string,
    value: string | boolean
  ) => {
    setFormData(prev => ({
      ...prev,
      [form]: {
        ...prev[form],
        [field]: value,
      },
    }));
    setSaveStatus('idle');
  };

  // ...

  const handleSave = async () => {
    if (!caseId) {
      console.error('Cannot save form: caseId is undefined');
      alert('Error: Case ID is not available. Please refresh the page.');
      return;
    }

    setIsSaving(true);
    setSaveStatus('saving');
    try {
      await formsApi.saveFormData(caseId, activeForm, formData[activeForm]);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error('Failed to save form:', error);
      setSaveStatus('error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRefill = async () => {
    if (!caseId) {
      console.error('Cannot refill form: caseId is undefined');
      alert('Error: Case ID is not available. Please refresh the page.');
      return;
    }

    if (
      !confirm(
        'This will overwrite current form data with AI extracted data. Continue?'
      )
    )
      return;

    setIsRefilling(true);
    try {
      const data = await formsApi.refillFormData(caseId, activeForm);
      // The backend returns the full merged data object
      if (data) {
        // Sanitize nulls
        const sanitizedData = Object.entries(data).reduce(
          (acc, [key, val]) => ({
            ...acc,
            [key]: val === null ? '' : val,
          }),
          {}
        );

        setFormData(prev => ({
          ...prev,
          [activeForm]: {
            ...prev[activeForm],
            ...sanitizedData,
          },
        }));
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 3000);
      }
    } catch (error) {
      console.error('Failed to refill form:', error);
      alert('Failed to refill form data');
    } finally {
      setIsRefilling(false);
    }
  };

  const handleDownloadPDF = async (
    formType: 'i140' | 'i129' | 'i907' | 'g28'
  ) => {
    if (!caseId) {
      console.error('Cannot download PDF: caseId is undefined');
      alert('Error: Case ID is not available. Please refresh the page.');
      return;
    }

    try {
      // First save current data to ensure PDF is up to date
      await formsApi.saveFormData(caseId, formType, formData[formType]);

      const blob = await formsApi.downloadPdf(caseId, formType);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${formType.toUpperCase()}_${caseData?.applicantName?.replace(/\s+/g, '_') || 'Form'}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to download PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  const handleDownloadXFDF = async (
    formType: 'i140' | 'i129' | 'i907' | 'g28'
  ) => {
    if (!caseId) {
      console.error('Cannot download XFDF: caseId is undefined');
      alert('Error: Case ID is not available. Please refresh the page.');
      return;
    }

    try {
      // First save current data
      await formsApi.saveFormData(caseId, formType, formData[formType]);

      const blob = await formsApi.downloadXfdf(caseId, formType);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${formType.toUpperCase()}_${caseData?.applicantName?.replace(/\s+/g, '_') || 'Form'}_data.xfdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to download XFDF:', error);
      alert('Failed to generate XFDF data. Please try again.');
    }
  };

  const handlePreviewPDF = async () => {
    if (!caseId) {
      console.error('Cannot preview PDF: caseId is undefined');
      alert('Error: Case ID is not available. Please refresh the page.');
      return;
    }

    setIsLoadingPreview(true);
    try {
      // First save current data
      await formsApi.saveFormData(caseId, activeForm, formData[activeForm]);

      const response = await formsApi.getFormPreview(caseId, activeForm);
      if (response.success && response.data?.base64) {
        setPreviewData(response.data.base64);
        setPreviewInfo({
          supportsFilling: response.data.supportsFilling,
          filledFieldCount: response.data.filledFieldCount,
          totalFieldCount: response.data.totalFieldCount,
        });
        setShowPreview(true);
      } else {
        alert('Failed to generate preview');
      }
    } catch (error) {
      console.error('Failed to generate preview:', error);
      alert('Failed to generate PDF preview');
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleSaveToCase = async () => {
    if (!caseId) {
      console.error('Cannot save to case: caseId is undefined');
      alert('Error: Case ID is not available. Please refresh the page.');
      return;
    }

    if (
      !confirm(
        'Save this filled form as a document in the case? This will create a new document entry.'
      )
    )
      return;

    setIsSavingToCase(true);
    try {
      // First save current data
      await formsApi.saveFormData(caseId, activeForm, formData[activeForm]);

      const response = (await formsApi.saveFormToCase(caseId, activeForm)) as {
        success: boolean;
        document?: { fileName: string };
      };
      if (response.success) {
        alert(
          `Form saved to case documents: ${response.document?.fileName || 'Success'}`
        );
      } else {
        alert('Failed to save form to case');
      }
    } catch (error) {
      console.error('Failed to save form to case:', error);
      alert('Failed to save form to case documents');
    } finally {
      setIsSavingToCase(false);
    }
  };

  const renderI140Form = () => {
    const currentStep = I140_STEPS[i140Step];

    const renderI140StepContent = () => {
      switch (currentStep.id) {
        case 'personal':
          return (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Last Name *
                </label>
                <input
                  type="text"
                  value={formData.i140.beneficiaryLastName}
                  onChange={e =>
                    handleInputChange(
                      'i140',
                      'beneficiaryLastName',
                      e.target.value
                    )
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  First Name *
                </label>
                <input
                  type="text"
                  value={formData.i140.beneficiaryFirstName}
                  onChange={e =>
                    handleInputChange(
                      'i140',
                      'beneficiaryFirstName',
                      e.target.value
                    )
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Middle Name
                </label>
                <input
                  type="text"
                  value={formData.i140.beneficiaryMiddleName}
                  onChange={e =>
                    handleInputChange(
                      'i140',
                      'beneficiaryMiddleName',
                      e.target.value
                    )
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          );
        case 'birth':
          return (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Date of Birth *
                </label>
                <input
                  type="date"
                  value={formData.i140.dateOfBirth}
                  onChange={e =>
                    handleInputChange('i140', 'dateOfBirth', e.target.value)
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Country of Birth *
                </label>
                <input
                  type="text"
                  value={formData.i140.countryOfBirth}
                  onChange={e =>
                    handleInputChange('i140', 'countryOfBirth', e.target.value)
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Country of Citizenship *
                </label>
                <input
                  type="text"
                  value={formData.i140.countryOfCitizenship}
                  onChange={e =>
                    handleInputChange(
                      'i140',
                      'countryOfCitizenship',
                      e.target.value
                    )
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>
          );
        case 'immigration':
          return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Alien Registration Number (A-Number)
                </label>
                <input
                  type="text"
                  value={formData.i140.alienRegistrationNumber}
                  onChange={e =>
                    handleInputChange(
                      'i140',
                      'alienRegistrationNumber',
                      e.target.value
                    )
                  }
                  placeholder="A-"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Current Nonimmigrant Status
                </label>
                <input
                  type="text"
                  value={formData.i140.currentNonimmigrantStatus}
                  onChange={e =>
                    handleInputChange(
                      'i140',
                      'currentNonimmigrantStatus',
                      e.target.value
                    )
                  }
                  placeholder="e.g., H-1B, F-1, O-1"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Date of Last Arrival to U.S.
                </label>
                <input
                  type="date"
                  value={formData.i140.dateOfLastArrival}
                  onChange={e =>
                    handleInputChange(
                      'i140',
                      'dateOfLastArrival',
                      e.target.value
                    )
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  I-94 Number
                </label>
                <input
                  type="text"
                  value={formData.i140.i94Number}
                  onChange={e =>
                    handleInputChange('i140', 'i94Number', e.target.value)
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          );
        case 'passport':
          return (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Passport Number *
                </label>
                <input
                  type="text"
                  value={formData.i140.passportNumber}
                  onChange={e =>
                    handleInputChange('i140', 'passportNumber', e.target.value)
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Passport Country *
                </label>
                <input
                  type="text"
                  value={formData.i140.passportCountry}
                  onChange={e =>
                    handleInputChange('i140', 'passportCountry', e.target.value)
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Passport Expiration Date *
                </label>
                <input
                  type="date"
                  value={formData.i140.passportExpiration}
                  onChange={e =>
                    handleInputChange(
                      'i140',
                      'passportExpiration',
                      e.target.value
                    )
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>
          );
        case 'contact':
          return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-2">
                  Current Address *
                </label>
                <input
                  type="text"
                  value={formData.i140.currentAddress}
                  onChange={e =>
                    handleInputChange('i140', 'currentAddress', e.target.value)
                  }
                  placeholder="Street address"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">City *</label>
                <input
                  type="text"
                  value={formData.i140.city}
                  onChange={e =>
                    handleInputChange('i140', 'city', e.target.value)
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  State *
                </label>
                <input
                  type="text"
                  value={formData.i140.state}
                  onChange={e =>
                    handleInputChange('i140', 'state', e.target.value)
                  }
                  placeholder="e.g., CA, NY"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  ZIP Code *
                </label>
                <input
                  type="text"
                  value={formData.i140.zipCode}
                  onChange={e =>
                    handleInputChange('i140', 'zipCode', e.target.value)
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={formData.i140.email}
                  onChange={e =>
                    handleInputChange('i140', 'email', e.target.value)
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={formData.i140.phone}
                  onChange={e =>
                    handleInputChange('i140', 'phone', e.target.value)
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          );
        case 'classification':
          return (
            <div>
              <label className="block text-sm font-medium mb-2">
                Classification Requested *
              </label>
              <select
                value={formData.i140.classificationRequested}
                onChange={e =>
                  handleInputChange(
                    'i140',
                    'classificationRequested',
                    e.target.value
                  )
                }
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select classification...</option>
                <option value="EB-1A">
                  EB-1A: Alien of Extraordinary Ability
                </option>
                <option value="EB-1B">
                  EB-1B: Outstanding Professor or Researcher
                </option>
                <option value="EB-1C">
                  EB-1C: Multinational Manager or Executive
                </option>
                <option value="EB-2">
                  EB-2: Advanced Degree or Exceptional Ability
                </option>
                <option value="EB-2 NIW">
                  EB-2 NIW: National Interest Waiver
                </option>
                <option value="EB-3">EB-3: Skilled Worker, Professional</option>
              </select>
            </div>
          );
        default:
          return null;
      }
    };

    return (
      <div className="space-y-6">
        {/* Step Indicator */}
        <div className="flex items-center justify-between mb-8">
          {I140_STEPS.map((step, index) => (
            <div key={step.id} className="flex items-center flex-1">
              <button
                onClick={() => setI140Step(index)}
                className={`flex flex-col items-center flex-1 ${
                  index === i140Step
                    ? 'text-blue-600'
                    : index < i140Step
                      ? 'text-gray-700'
                      : 'text-gray-400'
                }`}
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium mb-1 ${
                    index === i140Step
                      ? 'bg-blue-600 text-white'
                      : index < i140Step
                        ? 'bg-white border-2 border-gray-400 text-gray-600'
                        : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {index + 1}
                </div>
                <span className="text-xs font-medium hidden sm:block">
                  {step.title}
                </span>
              </button>
              {index < I140_STEPS.length - 1 && (
                <div
                  className={`h-0.5 flex-1 mx-2 ${index < i140Step ? 'bg-gray-400' : 'bg-gray-200'}`}
                />
              )}
            </div>
          ))}
        </div>

        {/* Step Header */}
        <div className="mb-6">
          <h3 className="text-xl font-semibold">{currentStep.title}</h3>
          <p className="text-gray-600 text-sm">{currentStep.description}</p>
        </div>

        {/* Step Content */}
        {renderI140StepContent()}

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-6 border-t">
          <Button
            onClick={() => setI140Step(prev => Math.max(0, prev - 1))}
            variant="secondary"
            disabled={i140Step === 0}
          >
            Previous
          </Button>
          <div className="flex gap-2">
            {i140Step < I140_STEPS.length - 1 ? (
              <Button
                onClick={() =>
                  setI140Step(prev => Math.min(I140_STEPS.length - 1, prev + 1))
                }
                variant="primary"
              >
                Next
              </Button>
            ) : (
              <Button
                onClick={handleSave}
                variant="primary"
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Complete & Save'}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderI129Form = () => {
    const currentStep = I129_STEPS[i129Step];

    const renderI129StepContent = () => {
      switch (currentStep.id) {
        case 'petitioner':
          return (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-3">
                  <RiAlertLine
                    className="text-blue-600 mt-1 flex-shrink-0"
                    size={20}
                  />
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-1">
                      O-1 Nonimmigrant Worker Petition
                    </h4>
                    <p className="text-sm text-blue-800">
                      Form I-129 is used to petition for O-1A or O-1B
                      classification.
                    </p>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    value={formData.i129.petitionerName}
                    onChange={e =>
                      handleInputChange(
                        'i129',
                        'petitionerName',
                        e.target.value
                      )
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Federal Employer ID (EIN)
                  </label>
                  <input
                    type="text"
                    value={formData.i129.petitionerEIN}
                    onChange={e =>
                      handleInputChange('i129', 'petitionerEIN', e.target.value)
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="XX-XXXXXXX"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={formData.i129.petitionerPhone}
                    onChange={e =>
                      handleInputChange(
                        'i129',
                        'petitionerPhone',
                        e.target.value
                      )
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">
                    Address *
                  </label>
                  <input
                    type="text"
                    value={formData.i129.petitionerAddress}
                    onChange={e =>
                      handleInputChange(
                        'i129',
                        'petitionerAddress',
                        e.target.value
                      )
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    City *
                  </label>
                  <input
                    type="text"
                    value={formData.i129.petitionerCity}
                    onChange={e =>
                      handleInputChange(
                        'i129',
                        'petitionerCity',
                        e.target.value
                      )
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    State *
                  </label>
                  <input
                    type="text"
                    value={formData.i129.petitionerState}
                    onChange={e =>
                      handleInputChange(
                        'i129',
                        'petitionerState',
                        e.target.value
                      )
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., CA, NY"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    ZIP Code *
                  </label>
                  <input
                    type="text"
                    value={formData.i129.petitionerZipCode}
                    onChange={e =>
                      handleInputChange(
                        'i129',
                        'petitionerZipCode',
                        e.target.value
                      )
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Contact Name
                  </label>
                  <input
                    type="text"
                    value={formData.i129.petitionerContactName}
                    onChange={e =>
                      handleInputChange(
                        'i129',
                        'petitionerContactName',
                        e.target.value
                      )
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Contact Title
                  </label>
                  <input
                    type="text"
                    value={formData.i129.petitionerContactTitle}
                    onChange={e =>
                      handleInputChange(
                        'i129',
                        'petitionerContactTitle',
                        e.target.value
                      )
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          );
        case 'beneficiary':
          return (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Last Name *
                </label>
                <input
                  type="text"
                  value={formData.i129.beneficiaryLastName}
                  onChange={e =>
                    handleInputChange(
                      'i129',
                      'beneficiaryLastName',
                      e.target.value
                    )
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  First Name *
                </label>
                <input
                  type="text"
                  value={formData.i129.beneficiaryFirstName}
                  onChange={e =>
                    handleInputChange(
                      'i129',
                      'beneficiaryFirstName',
                      e.target.value
                    )
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Middle Name
                </label>
                <input
                  type="text"
                  value={formData.i129.beneficiaryMiddleName}
                  onChange={e =>
                    handleInputChange(
                      'i129',
                      'beneficiaryMiddleName',
                      e.target.value
                    )
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Date of Birth *
                </label>
                <input
                  type="date"
                  value={formData.i129.dateOfBirth}
                  onChange={e =>
                    handleInputChange('i129', 'dateOfBirth', e.target.value)
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Country of Birth *
                </label>
                <input
                  type="text"
                  value={formData.i129.countryOfBirth}
                  onChange={e =>
                    handleInputChange('i129', 'countryOfBirth', e.target.value)
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Country of Citizenship *
                </label>
                <input
                  type="text"
                  value={formData.i129.countryOfCitizenship}
                  onChange={e =>
                    handleInputChange(
                      'i129',
                      'countryOfCitizenship',
                      e.target.value
                    )
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>
          );
        case 'classification':
          return (
            <div>
              <label className="block text-sm font-medium mb-2">
                O-1 Classification *
              </label>
              <select
                value={formData.i129.classificationRequested}
                onChange={e =>
                  handleInputChange(
                    'i129',
                    'classificationRequested',
                    e.target.value
                  )
                }
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select classification...</option>
                <option value="O-1A">
                  O-1A: Extraordinary Ability in Sciences, Arts, Education,
                  Business, or Athletics
                </option>
                <option value="O-1B">
                  O-1B: Extraordinary Achievement in Motion Picture or
                  Television
                </option>
              </select>
            </div>
          );
        case 'employment':
          return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-2">
                  Job Title *
                </label>
                <input
                  type="text"
                  value={formData.i129.jobTitle}
                  onChange={e =>
                    handleInputChange('i129', 'jobTitle', e.target.value)
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Start Date *
                </label>
                <input
                  type="date"
                  value={formData.i129.workStartDate}
                  onChange={e =>
                    handleInputChange('i129', 'workStartDate', e.target.value)
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  End Date *
                </label>
                <input
                  type="date"
                  value={formData.i129.workEndDate}
                  onChange={e =>
                    handleInputChange('i129', 'workEndDate', e.target.value)
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Wage Offered (Annual)
                </label>
                <input
                  type="text"
                  value={formData.i129.wageOffered}
                  onChange={e =>
                    handleInputChange('i129', 'wageOffered', e.target.value)
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., 150000"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Hours Per Week
                </label>
                <input
                  type="text"
                  value={formData.i129.hoursPerWeek}
                  onChange={e =>
                    handleInputChange('i129', 'hoursPerWeek', e.target.value)
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., 40"
                />
              </div>
            </div>
          );
        case 'location':
          return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-2">
                  Address
                </label>
                <input
                  type="text"
                  value={formData.i129.workLocationAddress}
                  onChange={e =>
                    handleInputChange(
                      'i129',
                      'workLocationAddress',
                      e.target.value
                    )
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">City</label>
                <input
                  type="text"
                  value={formData.i129.workLocationCity}
                  onChange={e =>
                    handleInputChange(
                      'i129',
                      'workLocationCity',
                      e.target.value
                    )
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">State</label>
                <input
                  type="text"
                  value={formData.i129.workLocationState}
                  onChange={e =>
                    handleInputChange(
                      'i129',
                      'workLocationState',
                      e.target.value
                    )
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  ZIP Code
                </label>
                <input
                  type="text"
                  value={formData.i129.workLocationZipCode}
                  onChange={e =>
                    handleInputChange(
                      'i129',
                      'workLocationZipCode',
                      e.target.value
                    )
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          );
        default:
          return null;
      }
    };

    return (
      <div className="space-y-6">
        {/* Step Indicator */}
        <div className="flex items-center justify-between mb-8">
          {I129_STEPS.map((step, index) => (
            <div key={step.id} className="flex items-center flex-1">
              <button
                onClick={() => setI129Step(index)}
                className={`flex flex-col items-center flex-1 ${
                  index === i129Step
                    ? 'text-blue-600'
                    : index < i129Step
                      ? 'text-gray-700'
                      : 'text-gray-400'
                }`}
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium mb-1 ${
                    index === i129Step
                      ? 'bg-blue-600 text-white'
                      : index < i129Step
                        ? 'bg-white border-2 border-gray-400 text-gray-600'
                        : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {index + 1}
                </div>
                <span className="text-xs font-medium hidden sm:block">
                  {step.title}
                </span>
              </button>
              {index < I129_STEPS.length - 1 && (
                <div
                  className={`h-0.5 flex-1 mx-2 ${index < i129Step ? 'bg-gray-400' : 'bg-gray-200'}`}
                />
              )}
            </div>
          ))}
        </div>

        {/* Step Header */}
        <div className="mb-6">
          <h3 className="text-xl font-semibold">{currentStep.title}</h3>
          <p className="text-gray-600 text-sm">{currentStep.description}</p>
        </div>

        {/* Step Content */}
        {renderI129StepContent()}

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-6 border-t">
          <Button
            onClick={() => setI129Step(prev => Math.max(0, prev - 1))}
            variant="secondary"
            disabled={i129Step === 0}
          >
            Previous
          </Button>
          <div className="flex gap-2">
            {i129Step < I129_STEPS.length - 1 ? (
              <Button
                onClick={() =>
                  setI129Step(prev => Math.min(I129_STEPS.length - 1, prev + 1))
                }
                variant="primary"
              >
                Next
              </Button>
            ) : (
              <Button
                onClick={handleSave}
                variant="primary"
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Complete & Save'}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderI907Form = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <RiAlertLine className="text-blue-600 mt-1 flex-shrink-0" size={20} />
          <div>
            <h4 className="font-semibold text-blue-900 mb-1">
              Premium Processing Service
            </h4>
            <p className="text-sm text-blue-800">
              Form I-907 requests premium processing for your I-140 petition.
              USCIS will process your case within 15 calendar days for an
              additional fee of $2,500.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
        <input
          type="checkbox"
          id="i907-enabled"
          checked={formData.i907.enabled}
          onChange={e => handleInputChange('i907', 'enabled', e.target.checked)}
          className="w-5 h-5"
        />
        <label htmlFor="i907-enabled" className="font-medium cursor-pointer">
          Request Premium Processing (I-907)
        </label>
      </div>

      {formData.i907.enabled && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              Beneficiary Name
            </label>
            <input
              type="text"
              value={formData.i907.beneficiaryName}
              onChange={e =>
                handleInputChange('i907', 'beneficiaryName', e.target.value)
              }
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Full name as it appears on I-140"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">
              I-140 Receipt Number (if available)
            </label>
            <input
              type="text"
              value={formData.i907.receiptNumber}
              onChange={e =>
                handleInputChange('i907', 'receiptNumber', e.target.value)
              }
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., LIN or SRC number"
            />
          </div>
        </div>
      )}
    </div>
  );

  const renderG28Form = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <RiAlertLine className="text-blue-600 mt-1 flex-shrink-0" size={20} />
          <div>
            <h4 className="font-semibold text-blue-900 mb-1">
              Attorney Representation
            </h4>
            <p className="text-sm text-blue-800">
              Form G-28 authorizes an attorney or accredited representative to
              represent you before USCIS. Complete this if you're working with
              legal counsel.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
        <input
          type="checkbox"
          id="g28-enabled"
          checked={formData.g28.enabled}
          onChange={e => handleInputChange('g28', 'enabled', e.target.checked)}
          className="w-5 h-5"
        />
        <label htmlFor="g28-enabled" className="font-medium cursor-pointer">
          Include Attorney Representation (G-28)
        </label>
      </div>

      {formData.g28.enabled && (
        <>
          <div>
            <h3 className="text-lg font-semibold mb-4">Attorney Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Attorney Last Name *
                </label>
                <input
                  type="text"
                  value={formData.g28.attorneyLastName}
                  onChange={e =>
                    handleInputChange('g28', 'attorneyLastName', e.target.value)
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required={formData.g28.enabled}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Attorney First Name *
                </label>
                <input
                  type="text"
                  value={formData.g28.attorneyFirstName}
                  onChange={e =>
                    handleInputChange(
                      'g28',
                      'attorneyFirstName',
                      e.target.value
                    )
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required={formData.g28.enabled}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-2">
                  Law Firm Name *
                </label>
                <input
                  type="text"
                  value={formData.g28.lawFirmName}
                  onChange={e =>
                    handleInputChange('g28', 'lawFirmName', e.target.value)
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required={formData.g28.enabled}
                />
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-4">Law Firm Address</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-2">
                  Address *
                </label>
                <input
                  type="text"
                  value={formData.g28.address}
                  onChange={e =>
                    handleInputChange('g28', 'address', e.target.value)
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required={formData.g28.enabled}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">City *</label>
                <input
                  type="text"
                  value={formData.g28.city}
                  onChange={e =>
                    handleInputChange('g28', 'city', e.target.value)
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required={formData.g28.enabled}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  State *
                </label>
                <input
                  type="text"
                  value={formData.g28.state}
                  onChange={e =>
                    handleInputChange('g28', 'state', e.target.value)
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required={formData.g28.enabled}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  ZIP Code *
                </label>
                <input
                  type="text"
                  value={formData.g28.zipCode}
                  onChange={e =>
                    handleInputChange('g28', 'zipCode', e.target.value)
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required={formData.g28.enabled}
                />
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-4">Contact & Licensing</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  value={formData.g28.phone}
                  onChange={e =>
                    handleInputChange('g28', 'phone', e.target.value)
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required={formData.g28.enabled}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={formData.g28.email}
                  onChange={e =>
                    handleInputChange('g28', 'email', e.target.value)
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required={formData.g28.enabled}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Bar Number *
                </label>
                <input
                  type="text"
                  value={formData.g28.barNumber}
                  onChange={e =>
                    handleInputChange('g28', 'barNumber', e.target.value)
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required={formData.g28.enabled}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  USCIS Online Account Number
                </label>
                <input
                  type="text"
                  value={formData.g28.USCISOnlineAccountNumber}
                  onChange={e =>
                    handleInputChange(
                      'g28',
                      'USCISOnlineAccountNumber',
                      e.target.value
                    )
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold">USCIS Forms</h2>
            <p className="text-gray-600 mt-1">
              Fill out required immigration forms for your petition package
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleRefill}
              disabled={isRefilling || isSaving}
              className="relative bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white border-0 shadow-lg hover:shadow-xl transition-all duration-300 animate-pulse-subtle"
            >
              {isRefilling ? (
                <>
                  <RiRefreshLine size={18} className="mr-1 animate-spin" />
                  Refilling...
                </>
              ) : (
                <>
                  <RiMagicLine size={18} className="mr-1" />
                  Refill with AI
                  <span className="absolute -top-1 -right-1 flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-yellow-500"></span>
                  </span>
                </>
              )}
            </Button>
            <Button
              onClick={handlePreviewPDF}
              disabled={isLoadingPreview || isSaving}
              variant="secondary"
            >
              {isLoadingPreview ? (
                <>Loading...</>
              ) : (
                <>
                  <RiEyeLine size={18} className="mr-1" />
                  Preview PDF
                </>
              )}
            </Button>
            <Button
              onClick={handleSaveToCase}
              disabled={isSavingToCase || isSaving}
              variant="secondary"
            >
              {isSavingToCase ? (
                <>Saving...</>
              ) : (
                <>
                  <RiAttachmentLine size={18} className="mr-1" />
                  Save to Case
                </>
              )}
            </Button>
            <Button onClick={handleSave} disabled={isSaving} variant="primary">
              {saveStatus === 'saving' ? (
                <>Saving...</>
              ) : saveStatus === 'saved' ? (
                <>
                  <RiCheckLine size={18} />
                  Saved
                </>
              ) : (
                'Save Forms'
              )}
            </Button>
          </div>
        </div>

        {saveStatus === 'saved' && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
            <RiCheckLine className="text-green-600" size={18} />
            <span className="text-sm text-green-800">
              Forms saved successfully
            </span>
          </div>
        )}
      </Card>

      {/* Form Selection - Featured and Other */}
      <Card className="p-4">
        {/* Featured Forms */}
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-sm font-medium text-gray-500">
              Featured for {caseData?.visaType || 'this visa type'}
            </span>
            <span className="h-px flex-1 bg-gray-200"></span>
          </div>
          <div className="flex gap-2 flex-wrap">
            {featuredForms.map(form => (
              <button
                key={form.id}
                onClick={() => setActiveForm(form.id)}
                className={`px-4 py-3 rounded-lg font-medium transition-colors border-2 ${
                  activeForm === form.id
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                }`}
              >
                <div className="flex items-center gap-2">
                  <RiFileTextLine size={18} />
                  {form.fullName}
                  {form.id === 'i907' && formData.i907.enabled && (
                    <span className="inline-block w-2 h-2 bg-green-400 rounded-full"></span>
                  )}
                  {form.id === 'g28' && formData.g28.enabled && (
                    <span className="inline-block w-2 h-2 bg-green-400 rounded-full"></span>
                  )}
                </div>
                <div className="text-xs mt-1 opacity-80">
                  {form.id === 'i907' ? getI907Description(caseData?.visaType) : form.description}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Other Forms - Expandable */}
        {otherForms.length > 0 && (
          <div>
            <button
              onClick={() => setShowOtherForms(!showOtherForms)}
              className="flex items-center gap-2 mb-3 w-full hover:text-gray-600 transition-colors"
            >
              {showOtherForms ? (
                <RiArrowDownSLine size={18} className="text-gray-400" />
              ) : (
                <RiArrowRightSLine size={18} className="text-gray-400" />
              )}
              <span className="text-sm font-medium text-gray-400">
                Other Forms ({otherForms.length})
              </span>
              <span className="h-px flex-1 bg-gray-100"></span>
            </button>
            {showOtherForms && (
              <div className="flex gap-2 flex-wrap animate-in fade-in slide-in-from-top-2 duration-200">
                {otherForms.map(form => (
                  <button
                    key={form.id}
                    onClick={() => setActiveForm(form.id)}
                    className={`px-4 py-3 rounded-lg font-medium transition-colors border ${
                      activeForm === form.id
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-gray-50 border-gray-200 hover:border-gray-300 hover:bg-gray-100 text-gray-600'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <RiFileTextLine size={16} />
                      {form.fullName}
                      {form.id === 'i907' && formData.i907.enabled && (
                        <span className="inline-block w-2 h-2 bg-green-400 rounded-full"></span>
                      )}
                      {form.id === 'g28' && formData.g28.enabled && (
                        <span className="inline-block w-2 h-2 bg-green-400 rounded-full"></span>
                      )}
                    </div>
                    <div className="text-xs mt-1 opacity-70">
                      {form.id === 'i907' ? getI907Description(caseData?.visaType) : form.description}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Form Content */}
      <Card className="p-6">
        {activeForm === 'i140' && renderI140Form()}
        {activeForm === 'i129' && renderI129Form()}
        {activeForm === 'i907' && renderI907Form()}
        {activeForm === 'g28' && renderG28Form()}
      </Card>

      {/* Additional Extracted Data Section */}
      {extraFields[activeForm] &&
        Object.keys(extraFields[activeForm]).length > 0 && (
          <Card className="p-6">
            <button
              onClick={() => setShowExtraFields(!showExtraFields)}
              className="flex items-center justify-between w-full text-left"
            >
              <div className="flex items-center gap-2">
                <RiMagicLine className="text-purple-600" size={20} />
                <h3 className="text-lg font-semibold text-gray-800">
                  Additional AI-Extracted Data
                </h3>
                <span className="text-sm text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                  {Object.keys(extraFields[activeForm]).length} fields
                </span>
              </div>
              {showExtraFields ? (
                <RiArrowDownSLine className="text-gray-400" size={24} />
              ) : (
                <RiArrowRightSLine className="text-gray-400" size={24} />
              )}
            </button>

            {showExtraFields && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm text-gray-600 mb-4">
                  These fields were extracted from your case documents by AI but
                  are not part of the standard form. They may be useful for
                  reference or for completing additional sections of the form.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(extraFields[activeForm]).map(
                    ([key, value]) => (
                      <div
                        key={key}
                        className="bg-gray-50 rounded-lg p-3 cursor-pointer hover:bg-gray-100 transition-colors group relative"
                        onClick={() => copyToClipboard(key, value)}
                        title="Click to copy"
                      >
                        <label className="block text-xs font-medium text-gray-500 mb-1">
                          {formatFieldName(key)}
                        </label>
                        <div className="text-sm text-gray-900 flex items-start justify-between gap-2">
                          <span className="break-words flex-1">
                            {typeof value === 'boolean'
                              ? value
                                ? 'Yes'
                                : 'No'
                              : typeof value === 'object'
                                ? JSON.stringify(value, null, 2)
                                : String(value || '-')}
                          </span>
                          <span className="flex-shrink-0 mt-0.5">
                            {copiedField === key ? (
                              <RiCheckLine
                                className="text-green-600"
                                size={16}
                              />
                            ) : (
                              <RiFileCopyLine
                                className="text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"
                                size={16}
                              />
                            )}
                          </span>
                        </div>
                        {copiedField === key && (
                          <span className="absolute -top-2 right-2 text-xs text-green-600 bg-white px-2 py-0.5 rounded shadow-sm">
                            Copied!
                          </span>
                        )}
                      </div>
                    )
                  )}
                </div>
              </div>
            )}
          </Card>
        )}

      {/* PDF Preview Modal */}
      {showPreview && previewData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h3 className="text-lg font-semibold">
                  Form {activeForm.toUpperCase()} Preview
                </h3>
                {previewInfo && previewInfo.supportsFilling && (
                  <p className="text-sm text-green-600">
                    Filled {previewInfo.filledFieldCount} of{' '}
                    {previewInfo.totalFieldCount} fields
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => handleDownloadPDF(activeForm)}
                  variant="secondary"
                  className="flex items-center gap-1"
                >
                  <RiDownloadLine size={16} />
                  Download
                </Button>
                <Button
                  onClick={handleSaveToCase}
                  disabled={isSavingToCase}
                  variant="secondary"
                  className="flex items-center gap-1"
                >
                  <RiAttachmentLine size={16} />
                  {isSavingToCase ? 'Saving...' : 'Save to Case'}
                </Button>
                <Button
                  onClick={() => {
                    setShowPreview(false);
                    setPreviewData(null);
                    setPreviewInfo(null);
                  }}
                  variant="secondary"
                >
                  <RiEyeOffLine size={16} />
                  Close
                </Button>
              </div>
            </div>

            {/* XFA Warning Banner */}
            {previewInfo && !previewInfo.supportsFilling && (
              <div className="bg-amber-50 border-b border-amber-200 p-4">
                <div className="flex items-start gap-3">
                  <RiAlertLine
                    className="text-amber-600 mt-0.5 flex-shrink-0"
                    size={20}
                  />
                  <div className="flex-1">
                    <h4 className="font-semibold text-amber-900">
                      Auto-fill not supported for this form
                    </h4>
                    <p className="text-sm text-amber-800 mt-1">
                      Form {activeForm.toUpperCase()} uses XFA format (XML Forms
                      Architecture) which cannot be auto-filled
                      programmatically. Your data has been saved. To complete
                      this PDF:
                    </p>
                    <ul className="text-sm text-amber-800 mt-2 list-disc list-inside">
                      <li>
                        Download the XFDF data file and import into Adobe
                        Acrobat Pro
                      </li>
                      <li>Or manually enter the data from the form editor</li>
                    </ul>
                    <div className="mt-3">
                      <Button
                        onClick={() => handleDownloadXFDF(activeForm)}
                        variant="secondary"
                        className="text-sm"
                      >
                        <RiFileCodeLine size={16} />
                        Download XFDF Data
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="flex-1 overflow-auto p-4 bg-gray-100">
              <Suspense
                fallback={
                  <div className="flex items-center justify-center h-96">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    <span className="ml-3 text-gray-600">
                      Loading PDF viewer...
                    </span>
                  </div>
                }
              >
                <PDFViewer base64Data={previewData} className="min-h-[600px]" />
              </Suspense>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormsTab;
